import { gql, useLazyQuery, useMutation, useQuery } from '@apollo/client'
import findLastIndex from 'lodash/findLastIndex'
import pick from 'lodash/pick'
import { useCallback, useEffect, useMemo } from 'react'

import { currentArtistVar } from '@/clients/utoniq-core'
import {
  ArchiveArtistThreadChannelMutation,
  ArchiveArtistThreadChannelMutationVariables,
  ArtistThreadChannelOrderByType,
  ArtistThreads,
  ArtistThreadsQuery,
  ArtistThreadsQueryVariables,
  BlockUser,
  BlockUserMutation,
  BlockUserMutationVariables,
  CommentBoxFragmentDoc,
  CommentsConnectionFragmentDoc,
  CreateArtistThreadCommentMutation,
  CreateArtistThreadCommentMutationVariables,
  DeleteArtistThreadCommentMutation,
  DeleteArtistThreadCommentMutationVariables,
  LikeArtistThreadChannelMutation,
  LikeArtistThreadChannelMutationVariables,
  LikeThreadCommentMutation,
  LikeThreadCommentMutationVariables,
  ThreadBoxFragmentDoc,
  ThreadDetailQuery,
  ThreadDetailQueryVariables,
  UnlikeArtistThreadChannelMutation,
  UnlikeArtistThreadChannelMutationVariables,
  UnlikeThreadCommentMutation,
  UnlikeThreadCommentMutationVariables,
  UpdateArtistThreadMuteStatusMutation,
  UpdateArtistThreadMuteStatusMutationVariables,
  UpdateThreadUnreadMutation,
  UpdateThreadUnreadMutationVariables,
  UserUnreadType,
} from '@/clients/utoniq-core/schema'
import { useInitialLoading } from '@/components/lib/initialLoading'
import { config } from '@/config'
import { useMessage, useReplyToComment, useRouter } from '@/core'
import { useAnalytics } from '@/core/analytics'
import { makeViewModel } from '@/core/architecture'
import { useCurrentUser } from '@/core/auth'
import { openReportForm } from '@/core/googleForm'
import { setBadgeCount } from '@/core/native/badge'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { user } = useCurrentUser()
  const { analytics } = useAnalytics()
  const { router, generatePath, routerPages } =
    useRouter<{ threadId: string }>()
  const {
    data: { replyToDisplayName, replyToAccountId, replyToCommentId },
    onReplyToReply,
    onRemoveReplyToReply,
  } = useReplyToComment()
  const { showError } = useMessage()
  const threadId = router.query.threadId

  const { data, loading, error, fetchMore, refetch } = useQuery<
    ThreadDetailQuery,
    ThreadDetailQueryVariables
  >(QUERY, {
    variables: { artistThreadChannelId: threadId },
    returnPartialData: true,
    onCompleted: data => {
      if (!data?.artistThreadChannel.artist) return
      currentArtistVar(
        pick(data?.artistThreadChannel.artist, [
          'id',
          'avatar',
          'displayName',
          'accountId',
          'formattedAccountId',
          'totalUnreadMentionCount',
        ]) ?? null
      )
    },
  })
  const artistId = data?.artistThreadChannel?.artistId ?? ''

  const [refetchArtistThreads] = useLazyQuery<
    ArtistThreadsQuery,
    ArtistThreadsQueryVariables
  >(ArtistThreads, {
    onCompleted() {
      router.goBack(
        generatePath(routerPages.ArtistThreads, {
          artistId,
        })
      )
    },
  })

  const [likeArtistThreadChannel] = useMutation<
    LikeArtistThreadChannelMutation,
    LikeArtistThreadChannelMutationVariables
  >(LIKE_ARTIST_THREAD_CHANNEL)

  const [unlikeArtistThreadChannel] = useMutation<
    UnlikeArtistThreadChannelMutation,
    UnlikeArtistThreadChannelMutationVariables
  >(UNLIKE_ARTIST_THREAD_CHANNEL)

  const [createArtistThreadComment] = useMutation<
    CreateArtistThreadCommentMutation,
    CreateArtistThreadCommentMutationVariables
  >(CREATE_ARTIST_THREAD_COMMENT)

  const [deleteArtistThreadComment] = useMutation<
    DeleteArtistThreadCommentMutation,
    DeleteArtistThreadCommentMutationVariables
  >(DELETE_ARTIST_THREAD_COMMENT)

  const [blockUser] = useMutation<
    BlockUserMutation,
    BlockUserMutationVariables
  >(BlockUser)
  const [deleteThread] = useMutation<
    ArchiveArtistThreadChannelMutation,
    ArchiveArtistThreadChannelMutationVariables
  >(DELETE_THREAD, {
    onCompleted() {
      router.goBack(
        generatePath(routerPages.ArtistThreads, {
          artistId,
        })
      )
    },
  })

  const [likeComment] = useMutation<
    LikeThreadCommentMutation,
    LikeThreadCommentMutationVariables
  >(LIKE_THREAD_COMMENT)
  const [unlikeComment] = useMutation<
    UnlikeThreadCommentMutation,
    UnlikeThreadCommentMutationVariables
  >(UNLIKE_THREAD_COMMENT)

  const [updateThreadUnread] = useMutation<
    UpdateThreadUnreadMutation,
    UpdateThreadUnreadMutationVariables
  >(UPDATE_THREAD_UNREAD)

  const [updateArtistThreadMuteStatus] = useMutation<
    UpdateArtistThreadMuteStatusMutation,
    UpdateArtistThreadMuteStatusMutationVariables
  >(UPDATE_ARTIST_THREAD_MUTE_STATUS)

  const handleUpdateThreadUnread = useCallback(async () => {
    if (user?.isAnonymous) return
    if (!artistId && !threadId) return
    /**
     * アクセス時に未読数の更新を行う
     * ネイティブの場合、バッジ数を更新する
     */
    await updateThreadUnread({
      variables: {
        inputs: {
          artistId,
          parentId: threadId,
          userUnreadType: UserUnreadType.ArtistThread,
        },
      },
      // no need to update new lastReadAt to graphql cache
      fetchPolicy: 'no-cache',
    }).then(res => {
      if (res.data?.updateUserUnread) {
        setBadgeCount(res.data?.updateUserUnread?.totalUnreadMentionCount ?? 0)
      }
    })
  }, [artistId, threadId, updateThreadUnread])

  useEffect(() => {
    if (loading || !user?.uid) return
    handleUpdateThreadUnread()
  }, [handleUpdateThreadUnread, loading, user?.uid])

  const isNextFetch = !!data?.artistThreadChannel

  /**
   * @description
   * 未読コメントの位置を表示するためのインデックス
   * 未読コメント(メンションではない)に対する要件・API・デザインがFIXするまで実装しない
   */
  const unreadIndex = useMemo<number | undefined>(() => {
    if (
      !data?.artistThreadChannel?.comments?.edges.length ||
      data?.artistThreadChannel?.userUnread?.lastReadAt === undefined
    ) {
      return undefined
    }
    const lastIndex = findLastIndex(
      data?.artistThreadChannel?.comments.edges,
      ({ node }) =>
        node.createdAt >= data?.artistThreadChannel?.userUnread?.lastReadAt
    )
    return lastIndex >= 0 ? lastIndex : undefined
  }, [
    data?.artistThreadChannel?.comments?.edges,
    data?.artistThreadChannel?.userUnread?.lastReadAt,
  ])

  return {
    state: {
      loading: useInitialLoading(data, loading && !isNextFetch, error),
      hasNextPage:
        data?.artistThreadChannel?.comments?.pageInfo?.hasNextPage || false,
      thread: data?.artistThreadChannel ?? null,
      comments: data?.artistThreadChannel?.comments ?? null,
      user: data?.user ?? null,
      replyToDisplayName,
      unreadIndex,
    },
    actions: {
      async onLikeOrUnlike(isLike, likeCount) {
        if (isLike) {
          await unlikeArtistThreadChannel({
            variables: { unlikeArtistThreadChannelId: threadId },
            optimisticResponse({ unlikeArtistThreadChannelId }) {
              return {
                unlikeArtistThreadChannel: {
                  __typename: 'ArtistThreadChannel',
                  id: unlikeArtistThreadChannelId,
                  isLike: false,
                  likeCount: --likeCount,
                },
              }
            },
          }).then(() => {
            analytics?.track('click_unlikeThread', {
              applicationKey: config.applicationKey,
              artistId,
              threadId,
            })
          })
          return
        }
        await likeArtistThreadChannel({
          variables: { likeArtistThreadChannelId: threadId },
          optimisticResponse({ likeArtistThreadChannelId }) {
            return {
              likeArtistThreadChannel: {
                __typename: 'ArtistThreadChannel',
                id: likeArtistThreadChannelId,
                isLike: true,
                likeCount: ++likeCount,
              },
            }
          },
        }).then(() => {
          analytics?.track('click_likeThread', {
            applicationKey: config.applicationKey,
            artistId,
            threadId,
          })
        })
      },
      async onReply(inputs, artistId) {
        await createArtistThreadComment({
          variables: {
            id: threadId,
            inputs: {
              artistId,
              replyToId: replyToCommentId,
              replyToAccountId,
              ...inputs,
            },
          },
        }).then(() => {
          analytics?.track('click_sendComment', {
            applicationKey: config.applicationKey,
            threadId,
            artistId,
            name: 'thread',
          })
        })
        await handleUpdateThreadUnread()
        onRemoveReplyToReply()
      },
      async onReportUserComment(contentId, userId, accountId) {
        await openReportForm({
          email: user?.email ?? '',
          contentType: 'thread-comments',
          contentId,
          userId,
          accountId,
        })
      },
      async onDeleteComment(commentId) {
        await deleteArtistThreadComment({
          variables: {
            id: threadId,
            commentId,
          },
        }).then(() => {
          analytics?.track('click_deleteComment', {
            applicationKey: config.applicationKey,
            threadId,
            artistId,
            name: 'thread',
          })
        })
      },
      async onBlockUserComment(userId) {
        await blockUser({ variables: { id: userId } })
        await refetch({
          artistThreadChannelId: threadId,
        })
      },
      async onBlockUser(userId) {
        await blockUser({ variables: { id: userId } })
        const artistId = data?.artistThreadChannel.artist?.id ?? ''
        await refetchArtistThreads({
          variables: {
            inputs: {
              artistId,
              options: { orderBy: ArtistThreadChannelOrderByType.CreatedAt },
            },
            artistId,
          },
        })
      },
      onReplyToReply,
      onRemoveReplyToReply,
      async onReportUser(contentId, userId, accountId) {
        await openReportForm({
          email: data?.user?.email ?? '',
          contentType: 'thread',
          contentId,
          userId,
          accountId,
        })
      },
      async onFetchMore() {
        await fetchMore({
          variables: {
            artistThreadChannelId: threadId,
            pageInput: {
              cursor: data?.artistThreadChannel?.comments?.pageInfo.endCursor,
              limit: 10,
            },
          },
        })
      },
      async onDeleteThread() {
        await deleteThread({
          variables: {
            archiveArtistThreadChannelId: threadId,
          },
        })
      },
      async onLikeOrUnlikeComment({ isLike, commentId, likeCount, id }) {
        if (isLike) {
          await unlikeComment({
            variables: {
              inputs: {
                id: id ?? '',
                commentId,
              },
            },
            optimisticResponse: ({ inputs }) => ({
              unlikeArtistThreadChannelComment: {
                __typename: 'Comment',
                id: inputs.commentId,
                isLike: false,
                likeCount: --likeCount,
              },
            }),
          })
          return
        }
        await likeComment({
          variables: {
            inputs: {
              id: id ?? '',
              commentId,
            },
          },
          optimisticResponse: ({ inputs }) => ({
            likeArtistThreadChannelComment: {
              __typename: 'Comment',
              id: inputs.commentId,
              isLike: true,
              likeCount: ++likeCount,
            },
          }),
        }).catch(e => showError(e))
      },
      async onUpdateArtistThreadMuteStatus() {
        await updateArtistThreadMuteStatus({
          variables: { id: threadId },
          optimisticResponse: () => ({
            updateArtistThreadMuteStatus: {
              __typename: 'ArtistThreadChannel',
              id: threadId,
              isAllowNotification:
                !data?.artistThreadChannel.isAllowNotification,
            },
          }),
        }).catch(e => showError(e))
      },
    },
  }
})

const QUERY = gql`
  query ThreadDetail($artistThreadChannelId: ID!, $pageInput: PageInput) {
    artistThreadChannel(id: $artistThreadChannelId) {
      ...ThreadBox
      comments(pageInput: $pageInput) {
        ...CommentsConnection
      }
    }
    user {
      id
      accountId
      formattedAccountId @client
      email
      avatar {
        uri
      }
    }
  }
  ${ThreadBoxFragmentDoc}
  ${CommentsConnectionFragmentDoc}
`

const LIKE_ARTIST_THREAD_CHANNEL = gql`
  mutation LikeArtistThreadChannel($likeArtistThreadChannelId: ID!) {
    likeArtistThreadChannel(id: $likeArtistThreadChannelId) {
      id
      likeCount
      isLike
    }
  }
`

const UNLIKE_ARTIST_THREAD_CHANNEL = gql`
  mutation UnlikeArtistThreadChannel($unlikeArtistThreadChannelId: ID!) {
    unlikeArtistThreadChannel(id: $unlikeArtistThreadChannelId) {
      id
      likeCount
      isLike
    }
  }
`

const CREATE_ARTIST_THREAD_COMMENT = gql`
  mutation CreateArtistThreadComment(
    $id: String!
    $inputs: CommunityCommentInput!
  ) {
    createArtistThreadComment(id: $id, inputs: $inputs) {
      ...CommentBox
    }
  }
  ${CommentBoxFragmentDoc}
`

const DELETE_ARTIST_THREAD_COMMENT = gql`
  mutation DeleteArtistThreadComment($id: String!, $commentId: String!) {
    deleteArtistThreadComment(id: $id, commentId: $commentId) {
      ...CommentBox
    }
  }
  ${CommentBoxFragmentDoc}
`
const DELETE_THREAD = gql`
  mutation ArchiveArtistThreadChannel($archiveArtistThreadChannelId: ID!) {
    archiveArtistThreadChannel(id: $archiveArtistThreadChannelId) {
      ...ThreadBox
    }
  }
  ${ThreadBoxFragmentDoc}
`

const LIKE_THREAD_COMMENT = gql`
  mutation LikeThreadComment($inputs: CommunityCommentLikeUnlikeInput!) {
    likeArtistThreadChannelComment(inputs: $inputs) {
      id
      isLike
      likeCount
    }
  }
`

const UNLIKE_THREAD_COMMENT = gql`
  mutation UnlikeThreadComment($inputs: CommunityCommentLikeUnlikeInput!) {
    unlikeArtistThreadChannelComment(inputs: $inputs) {
      id
      isLike
      likeCount
    }
  }
`

const UPDATE_THREAD_UNREAD = gql`
  mutation UpdateThreadUnread($inputs: UpdateUnreadInput!) {
    updateUserUnread(inputs: $inputs) {
      id
      lastReadAt
      parentId
      totalUnreadMentionCount
      unreadMentionCount
      unreadType
    }
  }
`

const UPDATE_ARTIST_THREAD_MUTE_STATUS = gql`
  mutation UpdateArtistThreadMuteStatus($id: ID!) {
    updateArtistThreadMuteStatus(id: $id) {
      id
      isAllowNotification
    }
  }
`
